// ---------------------------------------------------------------------------
// FormActions — footer button row for a form (props in → UI out).
//
// Groups a form's primary/secondary buttons in a flex row with consistent
// spacing. `align` controls horizontal placement: 'end' (default) pins the
// buttons to the trailing edge (the common save/cancel footer), 'start'
// keeps them leading, and 'between' spreads them apart (e.g. a destructive
// action on one side, confirm on the other).
//
// Styled only via the token layer (shared/styles/components.css):
//   .form-actions           — flex row, gap, top spacing
//   .form-actions--end      — justify-content: flex-end (default)
//   .form-actions--start    — justify-content: flex-start
//   .form-actions--between  — justify-content: space-between
//
// No data fetching, no auth logic, no surface assumptions. Safe for the
// public, sessionless register bundle.
// ---------------------------------------------------------------------------

type FormActionsAlign = 'start' | 'end' | 'between'

type FormActionsProps = {
  /** Action buttons rendered in the row (in DOM order). */
  children: React.ReactNode
  /** Horizontal alignment of the buttons. Defaults to 'end'. */
  align?: FormActionsAlign
}

const ALIGN_CLASS: Record<FormActionsAlign, string> = {
  start: 'form-actions--start',
  end: 'form-actions--end',
  between: 'form-actions--between',
}

/**
 * Presentational form-actions footer.
 *
 * Usage:
 *   <FormActions>
 *     <Button variant="ghost" onClick={cancel}>{t('common.cancel')}</Button>
 *     <Button variant="primary" type="submit">{t('common.save')}</Button>
 *   </FormActions>
 */
export function FormActions({ children, align = 'end' }: FormActionsProps) {
  return (
    <div className={`form-actions ${ALIGN_CLASS[align]}`}>
      {children}
    </div>
  )
}
